import type { Ssm5FreezeFrameReadout } from '@packages/domain/ssm5';

import { colors } from '@packages/core/styles';
import { Icon, Typography } from '@packages/ui/shared';
import { TablePagination } from '@web/components/table';
import { ToastNotification } from '@web/components/toast-notification';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { freezeFrameColumns } from '../helpers/freeze-frame-columns';
import { DEFAULT_FREEZE_FRAME_PAGE_SIZE, freezeFramePage } from '../helpers/freeze-frame-pages';
import { searchFreezeFrameParameters } from '../helpers/freeze-frame-search';
import { useSsm5FreezeFramesDocument } from '../hooks/use-ssm5-freeze-frames-document';
import { ssm5TextStyles } from '../ssm5.styles';
import { Ssm5FreezeFramesTable } from './ssm5-freeze-frames-table';
import { Ssm5FreezeFramesToolbar } from './ssm5-freeze-frames-toolbar';

/** Props for {@link Ssm5FreezeFramesReadout}. */
export type Ssm5FreezeFramesReadoutProps = {
    /** The Cloud Connector ID of the session the document is asked through. */
    cloudConnectorId: string;
    /** The freeze frame data of the DTC. */
    readout: Ssm5FreezeFrameReadout;
};

/** The borderless toast's radius, two design shadows and unrestricted width. */
const TOAST_STYLES = {
    border: 0,
    borderRadius: 4,
    boxShadow: '-4px -4px 12px rgba(0, 0, 0, 0.04), 4px 4px 12px rgba(0, 0, 0, 0.05)',
    maxWidth: 'none',
};

const useStyles = createUseStyles({
    noPaging: { paddingBottom: 25 },
    paging: { boxShadow: `inset 0 1px 0 ${colors.grayThree}`, flexShrink: 0, padding: '0 24px' },
    readout: { display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0, minWidth: 0 },
    toastBody: { alignItems: 'center', display: 'flex', gap: 8 },
});

/**
 * The loaded PID search, table, paging bar and document download feedback.
 *
 * @param props - {@link Ssm5FreezeFramesReadoutProps}
 * @returns the loaded page body
 */
export function Ssm5FreezeFramesReadout({ cloudConnectorId, readout }: Ssm5FreezeFramesReadoutProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    const [query, setQuery] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_FREEZE_FRAME_PAGE_SIZE);

    const { dismiss, download, isDownloaded } = useSsm5FreezeFramesDocument(
        cloudConnectorId,
        { dtcCode: readout.dtcCode, module: readout.module },
        query
    );
    const columns = freezeFrameColumns(readout.frames);
    const parameters = searchFreezeFrameParameters(readout.parameters, query);
    const page = freezeFramePage(parameters, pageIndex, pageSize);
    const hasResults = page.total > 0;

    const changeQuery = useCallback((text: string) => {
        setQuery(text);
        setPageIndex(0);
    }, []);
    const changeSize = useCallback((size: number) => {
        setPageSize(size);
        setPageIndex(0);
    }, []);

    return (
        <div className={classNames(classes.readout, { [classes.noPaging]: !hasResults })}>
            <Ssm5FreezeFramesToolbar
                isDownloadEnabled={hasResults}
                onDownload={download}
                onQueryChange={changeQuery}
                query={query}
            />
            <Ssm5FreezeFramesTable columns={columns} rows={page.rows} />
            {hasResults && (
                <div className={classes.paging}>
                    <TablePagination
                        displayCount={t('ssm5:ffd.count', { count: page.total, first: page.first, last: page.last })}
                        id="ssm5-freeze-frames-pagination"
                        look="tokens"
                        onClick={setPageIndex}
                        onSizeChange={changeSize}
                        page={pageIndex}
                        size={pageSize}
                        total={page.total}
                    />
                </div>
            )}
            <ToastNotification
                body={
                    <div className={classes.toastBody}>
                        <Icon color="white" name="checkmark" size={24} />
                        <Typography color="white" style={ssm5TextStyles.bodySemibold} variant="default">
                            {t('ssm5:ffd.download.done')}
                        </Typography>
                    </div>
                }
                onClose={dismiss}
                show={isDownloaded}
                styles={TOAST_STYLES}
                variant="success"
            />
        </div>
    );
}
